/**
 * How a role and its sign-in surface are SPOKEN — shared by the "+ Add
 * user" door and the Users & Roles list.
 *
 * This module exists to break a cycle rather than to hoard constants:
 * the door renders the guest list, and the guest list needs the same
 * role vocabulary the door offers. One of them importing the other would
 * be circular, and duplicating the map would let one role be spelled two
 * ways in a single pane — which is exactly the confusion the unified
 * door was built to remove.
 *
 * The server remains the source of truth for WHICH roles exist; this is
 * only their English.
 */
import type { EnvironmentSpec } from "./cf-client";

/** Human labels for the server's role codes. An unknown code renders as
 *  itself rather than vanishing — a role we haven't labelled yet should
 *  still be visible, not silently blank. */
export const ROLE_LABEL: Record<string, string> = {
  family: "Family — sees the show home",
  "practice-author": "Practice author — catalogue + plans",
  planner: "Planner — plans only",
  librarian: "Librarian — catalogue only",
  carer: "Carer — records against plans",
  admin: "Admin — full access",
  auditor: "Auditor — read-only oversight",
};

export function roleLabel(role: string): string {
  return ROLE_LABEL[role] ?? role;
}

/**
 * The role's NAME, without the explanation `ROLE_LABEL` carries.
 *
 * One role, one spelling: Users & Roles showed "Admin" in the production
 * dropdown and "Practice author — catalogue + plans" in the environment grid
 * of the SAME dialog (Ed, 2026-09-18). Names label controls and chips; the
 * description belongs in a tooltip, where it explains without shouting.
 */
export function roleName(role: string): string {
  const label = ROLE_LABEL[role];
  if (!label) return role;
  const [name] = label.split(" — ");
  return name;
}

/**
 * Where someone of this role signs in, per the server's description of
 * the environment. Family go to Circle of Support; everyone else to the
 * planner. Both addresses come from the environment spec, never from a
 * constant here — the surfaces differ per environment (the TLD IS the
 * environment) and a hardcoded one would send a guest to the wrong world.
 */
export function surfaceFor(spec: EnvironmentSpec | undefined, role: string): string {
  if (!spec) return "";
  return role === "family" ? spec.familySurface : spec.staffSurface;
}
